import axios from "axios";
import { ENDPOINTS } from "constants/endpoints";
import { toast } from "react-toastify";

export const handleFetchAllBanks = async (currency) => {
    try {
        const resp = await axios.get(ENDPOINTS.getAllBanks(currency));
        return resp.data;
    } catch (error) {
        return error;
    }
};

export const handleVerifyAccount = async (accountNumber, bankCode, currency = "NGN", source) => {
    try {
        const resp = await axios.post(ENDPOINTS.verifyAccount(accountNumber, bankCode), {
            accountNumber,
            bankCode,
            currency,
            ...(source ? { source } : {}),
        });
        return resp.data;
    } catch (error) {
        return error;
    }
};

export const verifyUsdRoutingNumber = async ({
    businessId,
    routingNumber,
    currency,
    currencyTo,
    source,
    signal,
}) => {
    const response = await axios.post(
        "/v1/wallets/verifyBankDetail",
        {
            accountNumber: routingNumber,
            bankCode: "0000",
            currency,
            currencyTo,
            ...(source ? { source } : {}),
        },
        {
            headers: businessId ? { "x-business-id": businessId } : undefined,
            signal,
        }
    );
    const data = response?.data?.data ?? response?.data ?? {};
    const address = data?.bankAddress ?? data?.bank_address ?? {};

    return {
        accountName: String(data?.accountName ?? data?.account_name ?? "").trim(),
        bankName: String(data?.bankName ?? data?.bank_name ?? "").trim(),
        bankCode: String(data?.bankCode ?? data?.bank_code ?? "").trim(),
        bic: String(data?.bic ?? data?.swiftCode ?? data?.swift_code ?? "").trim(),
        bankStreet: String(
            address?.line1 ?? address?.street_line_1 ?? data?.bankStreetAddress ?? ""
        ).trim(),
        bankCity: String(address?.city ?? data?.bankCity ?? "").trim(),
        bankState: String(address?.state ?? data?.bankState ?? "").trim(),
        bankZip: String(
            address?.zip ?? address?.postalCode ?? address?.postal_code ?? data?.bankZipCode ?? ""
        ).trim(),
        bankCountry: String(
            address?.country ?? data?.bankCountry ?? data?.countryCode ?? ""
        ).trim(),
    };
};

// Ask for the update on the transaction fee
export const getTransactionFee = async () => {
    try {
        const response = await axios.get(ENDPOINTS.getTransactionFee);
        return response?.data;
    } catch (error) {
        return error;
    }
};

export const handleTransferFunds = async (isLocalTransfer, data) => {
    try {
        const response = await axios.post(
            isLocalTransfer ? ENDPOINTS.transferFunds : ENDPOINTS.internationalTransfer,
            data
        );
        return response?.data;
    } catch (error) {
        return error;
    }
};

export const handleVerifyPin = async (data) => {
    try {
        const response = await axios.post(ENDPOINTS.verifyPin, data);
        return response?.data;
    } catch (error) {
        return error;
    }
};

export const handleSaveBeneficiary = async (data) => {
    try {
        const response = await axios.post(ENDPOINTS.saveBeneficiary, data);
        return response?.data;
    } catch (error) {
        return error;
    }
};

export const handleGetBeneficiary = async (currency) => {
    try {
        const response = await axios.get(ENDPOINTS.getBeneficiary(currency));
        return response?.data;
    } catch (error) {
        return error;
    }
};

export const handleGetInvoiceSummary = async (data) => {
    const { businessId, ...rest } = data;
    try {
        const response = await axios.get(ENDPOINTS.getInvoiceSummary, {
            headers: {
                "x-business-id": businessId,
            },
            params: {
                ...rest,
            },
        });
        return response?.data;
    } catch (error) {
        return error;
    }
};

export const handleFetchAccounts = async (data) => {
    try {
        const response = await axios.get(ENDPOINTS.fetchAccounts, {
            headers: {
                "x-business-id": data.businessId,
            },
        });
        return response?.data?.data;
    } catch (error) {
        return error;
    }
};

export const handleGetAllInvoice = async (data) => {
    const { businessId, ...rest } = data;
    if (rest.statuses === "ALL") {
        delete rest.statuses;
    }

    try {
        const response = await axios.get(ENDPOINTS.getAllInvoice, {
            headers: {
                "x-business-id": businessId,
            },
            params: {
                ...rest,
            },
        });
        return response?.data;
    } catch (error) {
        return error;
    }
};
export const handleGetSingleInvoice = async (data) => {
    try {
        const response = await axios.get(ENDPOINTS.getSingleInvoice(data?.invoiceId), {
            headers: {
                "x-business-id": data.businessId,
            },
            params: {
                currency: data.currency,
                ...data,
            },
        });
        return response?.data?.data;
    } catch (error) {
        return error;
    }
};

export const handleGetWallets = async (data) => {
    try {
        const response = await axios.get(ENDPOINTS.getAllWallets, {
            headers: {
                "x-business-id": data.businessId,
            },
            signal: data.signal,
            params: {
                currency: data.currency,
            },
        });
        return response?.data?.data;
    } catch (error) {
        return error;
    }
};

export const handleGetWalletAccounts = async (data) => {
    try {
        const response = await axios.get(ENDPOINTS.getAllWallets + "s", {
            headers: {
                "x-business-id": data.businessId,
            },
        });
        return response?.data?.data;
    } catch (error) {
        return error;
    }
};

export const handleMakePayment = async (data) => {
    try {
        const response = await axios.post(ENDPOINTS.makeInvoicePayment(data?.invoiceId), {
            amount: data?.amount,
            pin: data?.pin,
        });
        return response?.data;
    } catch (error) {
        return error;
    }
};
export const handleDeleteSingleInvoice = async (invoiceId) => {
    try {
        const response = await axios.delete(ENDPOINTS.deleteInvoice(invoiceId));
        return response?.data;
    } catch (error) {
        return error;
    }
};

export const handleRejectSingleInvoice = async (invoiceId) => {
    try {
        const response = await axios.post(ENDPOINTS.rejectInvoice(invoiceId));
        return response?.data;
    } catch (error) {
        return error;
    }
};
export const handleGetAllUsers = async (data) => {
    try {
        const response = await axios.get(ENDPOINTS.getAllUsers(data?.type), {
            headers: {
                "x-business-id": data.businessId,
            },
        });
        return response?.data?.data;
    } catch (error) {
        return error;
    }
};

export const handleGetSingleUsers = async (data) => {
    try {
        const response = await axios.get(ENDPOINTS.getSingleUser(data?.id), {
            headers: {
                "x-business-id": data.businessId,
            },
        });
        return response?.data?.data;
    } catch (error) {
        return error;
    }
};

export const handleFetchStats = async (data) => {
    try {
        const response = await axios.get(ENDPOINTS.getStats, {
            headers: {
                "x-business-id": data.businessId,
            },
        });
        return response?.data;
    } catch (error) {
        return error;
    }
};

export const handleCreateInvoice = async (data) => {
    try {
        const response = await axios.post(ENDPOINTS.createInvoice, data);
        return response?.data;
    } catch (error) {
        return error?.response?.data || { message: "An error occurred", error };
    }
};

export const handleSendReminder = async (invoiceIdToEdit) => {
    try {
        const response = await axios.post(ENDPOINTS.reminder(invoiceIdToEdit));
        return response?.data;
    } catch (error) {
        toast.error(
            error?.response?.data?.message || error?.response?.data || "Failed to download pdf"
        );
        return error?.response?.data || { message: "An error occurred", error };
    }
};

export const handleDownloadInvoice = async (invoiceIdToEdit) => {
    try {
        const response = await axios.get(ENDPOINTS.downloadInvoice(invoiceIdToEdit), {
            responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `receipt-${invoiceIdToEdit}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return response?.data;
    } catch (error) {
        toast.error(
            error?.response?.data?.message || error?.response?.data || "Failed to download pdf"
        );
        return error?.response?.data || { message: "An error occurred", error };
    }
};

export const handleUploadBusinessDocuments = async (businessId, businessType, fileKey, file) => {
    try {
        const formData = new FormData();
        formData.append(fileKey, file, file.name);

        const response = await axios.post(
            ENDPOINTS.uploadBusinessDocuments(businessId, businessType),
            formData
        );
        return response?.data;
    } catch (error) {
        return error?.response?.data || { message: "An error occurred while uploading the file" };
    }
};

export const handleCheckEmail = async (email) => {
    try {
        const response = await axios.post(`/v1/users/check-email`, { email });
        return response?.data;
    } catch (error) {
        return error?.response?.data || { message: "An error occurred while checking the email" };
    }
};

// Function to register a user
export const handleRegisterUser = async (data) => {
    try {
        const response = await axios.post("/v1/users/register", data);
        return response?.data;
    } catch (error) {
        return error?.response?.data || { message: "An error occurred during registration" };
    }
};

// Function to send verification email
export const handleSendVerificationEmail = async (email, firstName, phoneNumber) => {
    try {
        const response = await axios.post("/v1/users/send-verification-email", {
            email,
            firstName,
            phoneNumber,
        });
        return response?.data;
    } catch (error) {
        return error?.response?.data || { message: "An error occurred while sending the email" };
    }
};

export const handleUploadFile = async ({ businessId, businessType, fileKey, file }) => {
    try {
        const formData = new FormData();
        formData.append(fileKey, file, file.name);

        const response = await axios.post(
            ENDPOINTS.uploadBusinessDocuments(businessId, businessType),
            formData
        );

        return response?.data;
    } catch (error) {
        return error?.response?.data || { message: "An error occurred while uploading the file" };
    }
};

export const handleUploadFileUtils = async ({ selectedFile, name }) => {
    try {
        const formData = new FormData();
        formData.append("file", selectedFile);

        const response = await axios.post(ENDPOINTS.uploadFileLink, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        const url = response?.data;
        return url;
    } catch (error) {
        return error?.response?.data || { message: "An error occurred while uploading the file" };
    }
};

export const handleUpdateBusiness = async (businessId) => {
    try {
        const response = await axios.put(`/v1/business/current/${businessId}`);
        return response?.data;
    } catch (error) {
        return (
            error?.response?.data || { message: "An error occurred while updating the business" }
        );
    }
};

export const handleRequestVerification = async () => {
    try {
        const response = await axios.post(`/v1/business/request-verification`);
        return response?.data;
    } catch (error) {
        return (
            error?.response?.data || { message: "An error occurred during verification request" }
        );
    }
};

export const handleRecordPayment = async (orderId, paymentData) => {
    try {
        const response = await axios.post(`/v1/flex/order/payment/${orderId}`, paymentData);
        return response.data;
    } catch (error) {
        console.error("Error Paying for Order:", error);
        return error;
    }
};
export const handleInvoicePayment = async (orderId, paymentData) => {
    try {
        const response = await axios.post(`/v1/invoice/payment/${orderId}`, paymentData);
        return response.data;
    } catch (error) {
        console.error("Error Paying invoice:", error);
        return error;
    }
};

export const handleRecordShipmentPayment = async (orderId, paymentData) => {
    const response = await axios.post(`/v1/flex/shipment/record-payment/${orderId}`, paymentData);
    return response.data;
};

export const fetchOrderMessages = async (orderId) => {
    try {
        const { data } = await axios.get(`/v1/message/flex-order/${orderId}`);
        return data.messages;
    } catch (error) {
        console.error("Error fetching messages:", error);
        return error;
    }
};

export const fetchSingleOrder = async (orderId) => {
    try {
        const { data } = await axios.get(ENDPOINTS.getSingleFlexOrder(orderId));
        return data;
    } catch (error) {
        return error;
    }
};

export const createOrderMessage = async (orderId, message, file) => {
    const formData = new FormData();
    if (message.trim()) {
        formData.append("message", message);
    }
    if (file) {
        formData.append("files", file);
    }

    const response = await axios.post(`/v1/message/flex/create-order/${orderId}`, formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });

    return response.data;
};

export const getMessageReplies = async (messageId) => {
    try {
        const response = await axios.get(`/v1/message/replies/${messageId}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching replies:", error);
        return error;
    }
};

export const sendMessageReply = async (messageId, formData) => {
    try {
        const response = await axios.post(`/v1/message/reply/${messageId}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    } catch (error) {
        console.error("Error sending reply:", error);
        return error;
    }
};

export const fetchCurrencyConversionRate = async (baseCurrency, quoteCurrency) => {
    try {
        const response = await axios.post("/v1/wallets/currency/single_pair", {
            baseCurrency,
            quoteCurrency,
        });
        return response.data.data.baseCurrencyRate;
    } catch (error) {
        console.error("Error fetching conversion rate:", error?.message);
        return error;
    }
};

export const fetchBusinessData = async () => {
    try {
        const response = await axios.get("/v1/business/user");
        return response.data;
    } catch (error) {
        console.error("Error fetching business data:", error);
        return error;
    }
};

export const fetchCurrentBusiness = async () => {
    try {
        const response = await axios.get(`/v1/business/current`);
        return response.data;
    } catch (error) {
        console.error("Error fetching current business:", error);
        return error?.response?.data || { message: "Failed to fetch current business" };
    }
};

export const fetchUserProfile = async () => {
    try {
        const response = await axios.get("/v1/users/profile");
        return response?.data?.data;
    } catch (error) {
        console.error("Error fetching user profile:", error);
        return error.response?.data || { message: "An error occurred while fetching the profile" };
    }
};

export const handleUpdateUserNetworkStatus = async (data, type) => {
    try {
        const response = await axios[type === "edit" ? "put" : "delete"](
            ENDPOINTS.acceptUsers(data?.id),
            {
                headers: {
                    "x-business-id": data.businessId,
                },
            }
        );
        return response?.data?.data;
    } catch (error) {
        return error;
    }
};

export const handleDeleteUserNetworkStatus = async (data, type) => {
    try {
        const response = await axios.delete(ENDPOINTS.deleteUserFromNetwork(data?.id), {
            headers: {
                "x-business-id": data.businessId,
            },
        });
        return response?.data?.data;
    } catch (error) {
        return error;
    }
};

export const handleCreateShipment = async (data) => {
    try {
        const response = await axios.post(ENDPOINTS.createShipment, {
            ...data,
        });
        return response;
    } catch (error) {
        return error;
    }
};

export const handleAddShipmentDetails = async (data) => {
    try {
        const response = await axios.post(ENDPOINTS.addShipmentDetails(data.shipmentId), {
            shippingDetails: data.shipmentLegs,
        });
        return response;
    } catch (error) {
        return error;
    }
};

export const handleAddingPaymentDetails = async (data) => {
    const { shipmentId, ...rest } = data;
    try {
        const response = await axios.post(ENDPOINTS.addPaymentDetails(shipmentId), {
            ...rest,
        });
        return response;
    } catch (error) {
        return error;
    }
};

export const handleFetchCompletedOrders = async (data) => {
    try {
        const resp = await axios.get(ENDPOINTS.getAllCompletedOrders(data));
        return resp.data;
    } catch (error) {
        return error;
    }
};
export const handleFetchInboundOrders = async (data) => {
    const { forFinance, ...payload } = {
        ...data,
    };
    try {
        const resp = await axios.get(
            forFinance ? ENDPOINTS.getAllOutboundOrders : ENDPOINTS.getAllInboundOrders,
            { params: payload }
        );
        return resp.data;
    } catch (error) {
        return error;
    }
};

export const handleGetAllShipments = async ({ endpoint, searchTerm, currentPage }) => {
    const payload = {
        searchTerm,
        limit: 10,
        page: currentPage,
    };
    if (!searchTerm) {
        delete payload.searchTerm;
    }
    try {
        const resp = await axios.get(ENDPOINTS.getAllShipments(endpoint), {
            params: payload,
        });
        return resp.data;
    } catch (error) {
        return error;
    }
};

export const handleGetAllAssignedShipments = async ({ endpoint, searchTerm, currentPage }) => {
    try {
        const resp = await axios.get(ENDPOINTS.getAllAssignedShipments, {
            params: {
                searchTerm,
                limit: 10,
                page: currentPage,
            },
        });
        return resp.data;
    } catch (error) {
        return error;
    }
};

export const handleGetAllShipmentsStats = async () => {
    try {
        const resp = await axios.get(ENDPOINTS.getShipmentsStats);
        return resp.data;
    } catch (error) {
        return error;
    }
};

export const handleGetAllShipmentMessages = async (shipmentId) => {
    try {
        const response = await axios.get(ENDPOINTS.fetchShipmentStatus(shipmentId));
        return response.data;
    } catch (error) {
        console.error("Error fetching messages:", error);
        return error;
    }
};

export const handleMarkShipperPayment = async (status, shipmentDetailsId, shipmentId) => {
    try {
        const response = await axios.put(ENDPOINTS.changeShipperPaymentStatus(shipmentId), {
            status,
            shipmentDetailId: shipmentDetailsId,
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching messages:", error);
        return error;
    }
};

export const handleChangeShipmentStatus = async (data, shipmentId) => {
    try {
        const response = await axios.put(ENDPOINTS.changeShipmentStatus(shipmentId), {
            ...data,
        });
        return response.data;
    } catch (error) {
        return error;
    }
};

export const handleCancelShipment = async (shipmentId) => {
    try {
        const response = await axios.put(ENDPOINTS.cancelShipment(shipmentId));
        return response.data;
    } catch (error) {
        return error;
    }
};

export const handleUploadShipmentDoc = async ({ link, name, shipmentId, originalFilename }) => {
    try {
        const response = await axios.post(ENDPOINTS.uploadShipmentDoc(shipmentId), {
            name,
            link,
            originalFilename,
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching messages:", error);
        return error;
    }
};

export const handleDuplicateShipment = async (shipmentId) => {
    try {
        const response = await axios.post(ENDPOINTS.duplicateShipment, {
            shipmentId,
        });
        return response.data;
    } catch (error) {
        return error;
    }
};

export const handleDuplicateInvoice = async (invoiceId) => {
    try {
        const response = await axios.post(ENDPOINTS.duplicateInvoice, {
            invoiceId,
        });
        return response.data;
    } catch (error) {
        return error;
    }
};

export const handleAcceptOrRejectShipment = async ({ shipmentId, status }) => {
    try {
        const response = await axios.put(ENDPOINTS.acceptOrRejectShipment(shipmentId), {
            status,
        });
        return response.data;
    } catch (error) {
        return error;
    }
};

export const handleGetAddressDetails = async (placeId) => {
    try {
        const response = await axios.post(ENDPOINTS.getPlaceDetails, {
            placeId,
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching messages:", error);
        return error;
    }
};

export const handleReceiverAcceptOrReject = async ({ shipmentId, status }) => {
    try {
        const response = await axios.put(ENDPOINTS.receiverAcceptOrRejectShipment(shipmentId), {
            status,
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching messages:", error);
        return error;
    }
};

export const handleEditShipment = async (data) => {
    try {
        const response = await axios.put(ENDPOINTS.editShipment, {
            ...data,
        });
        return response;
    } catch (error) {
        return error;
    }
};

export const handleEditShipper = async ({ shipmentId, data }) => {
    try {
        const response = await axios.post(ENDPOINTS.editShipper(shipmentId), data);
        return response;
    } catch (error) {
        return error;
    }
};

export const handleOpenWallet = async ({ currency, businessId, ...rest }) => {
  const { paymentCurrency } = rest;
  const payload = {
    currency
  }
  if (paymentCurrency) {
    payload.paymentCurrency = paymentCurrency;
  }
    try {
        const token = localStorage.getItem("token");

        const response = await axios.post(
            ENDPOINTS.openWallet,
          payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "x-business-id": businessId,
                },
            }
        );

        toast.success("Wallet opened successfully");
        return response.data;
    } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to open wallet. Please try again.");
        return error;
    }
};

export const handleFetchTransactions = async (businessId) => {
    try {
        const response = await axios.get("/v1/wallets/transactions", {
            headers: {
                "x-business-id": businessId,
            },
        });
        return response.data;
    } catch (error) {
        return error?.response?.data || { message: "Failed to fetch transactions" };
    }
};

export const fetchChartData = async ({ businessId, currency, year }) => {
    const resp = await axios.get("/v1/wallets/chart-data", {
        headers: { "x-business-id": businessId },
        params: {
            year: year || new Date().getFullYear(),
            currency,
        },
    });
    return resp.data;
};

export const handleFetchWalletTransactions = async ({
    businessId,
    page = 1,
    limit = 20,
    status = [],
    transactionType = [],
    fromDate,
    toDate,
    currency,
    searchTerm,
    order,
}) => {
    const params = { page, limit };

    if (status.length) {
        params.status = status.join(",");
    }
    if (transactionType.length) params.transactionType = transactionType.join(",");
    if (fromDate) {
        params.fromDate = fromDate.toISOString ? fromDate.toISOString().slice(0, 10) : fromDate;
    }
    if (toDate) {
        params.toDate = toDate.toISOString ? toDate.toISOString().slice(0, 10) : toDate;
    }
    if (currency) {
        params.currency = currency;
    }
    if (searchTerm) {
        params.searchTerm = searchTerm;
    }

    if (order) {
        params.order = order;
    }

    const resp = await axios.get("/v1/wallets/transactions/dashboard", {
        headers: { "x-business-id": businessId },
        params,
    });
    return resp.data.data;
};

export async function handleGetWalletBeneficiaries({ businessId, currency, sortBy, search }) {
    const params = { currency };
    if (sortBy) params.sortBy = sortBy;
    if (search) params.search = search;

    const resp = await axios.get("/v1/beneficiary/all", {
        headers: { "x-business-id": businessId },
        params,
    });

    return resp.data.data;
}

export const handleDeleteBeneficiary = async ({ businessId, beneficiaryId }) => {
    try {
        const resp = await axios.delete(`/v1/beneficiary/${beneficiaryId}`, {
            headers: {
                "x-business-id": businessId,
            },
        });
        return resp.data.data;
    } catch (error) {
        throw error?.response?.data || error;
    }
};

export const fetchAllBanks = async ({ currencyFrom, businessId, currencyTo, source }) => {
    try {
        const query = new URLSearchParams({
            currency: currencyFrom,
            currencyTo,
            ...(source ? { source } : {}),
        });
        const response = await axios.get(`/v1/wallets/get_all_banks/all?${query.toString()}`, {
            headers: {
                "x-business-id": businessId,
            },
        });

        return response.data?.data || [];
    } catch (error) {
        console.error("Error fetching all banks:", error);
        return [];
    }
};

export const handleAddBeneficiary = async ({ businessId, data }) => {
    // API expects url-encoded form data
    const body = new URLSearchParams(data);

    const res = await axios.post("/v1/beneficiary", body, {
        headers: { "x-business-id": businessId },
    });

    return res.data;
};

export const handleSaveBeneficiaryStatus = async ({ businessId, data }) => {
    const res = await axios.put(ENDPOINTS.saveBeneficiaryStatus, data, {
        headers: { "x-business-id": businessId },
    });

    return res.data;
};

export const generateBankStatement = async ({
    currency,
    fromDate,
    toDate,
    businessId,
    isPassworded,
    pin,
    isFinance = false,
}) => {
    const params = new URLSearchParams({
        currency,
        fromDate,
        toDate,
        isPassworded,
        pin,
    });

    try {
        const response = await axios.get(
            !isFinance
                ? `/v1/wallets/mail-bank-statement?${params.toString()}`
                : `/v1/finance/new/mail-bank-statement?${params.toString()}`,
            {
                headers: {
                    "x-business-id": businessId,
                },
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const downloadBankStatement = async ({ currency, fromDate, toDate, pin, businessId }) => {
    const url = `/v1/wallets/bank-statement?currency=${currency}&fromDate=${fromDate}&toDate=${toDate}&pin=${pin}`;

    const response = await axios.get(url, {
        responseType: "blob",
        headers: {
            "x-business-id": businessId,
        },
    });

    const blob = new Blob([response.data], { type: "application/pdf" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `Bank_Statement_${currency}_${fromDate}_to_${toDate}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
};

export const fetchCoinNetworks = async ({ currency, businessId, normalize = true }) => {
    try {
        const resp = await axios.get("/v1/wallets/get-coins", {
            params: { currency },
            headers: { "x-business-id": businessId },
        });

        const data = resp?.data?.data || resp?.data || [];

        if (!normalize) return data;

        return data.map((n) => ({
            id: (n.id || n.code || n.network || n.name || "network").toString().toLowerCase(),
            name: n.name || n.network || n.code || "ERC20",
            label: n.shortName,
            network: n.network || "",
            accountNumber: n.accountNumber || n.address || n.walletAddress || "",
            icon: n.icon || null,
            fee: Number(n.fee ?? 0),
            balance: Number(n.balance ?? 0),
        }));
    } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to fetch coin networks");
        return [];
    }
};

export const fetchCryptoQuote = async ({
    fromCurrency,
    toCurrency,
    fromNetwork,
    toNetwork,
    amount,
    businessId,
}) => {
    try {
        const resp = await axios.get("/v1/wallets/crypto/quote", {
            params: {
                fromCurrency,
                toCurrency,
                fromNetwork,
                toNetwork,
                amount,
            },
            headers: businessId ? { "x-business-id": businessId } : undefined,
        });

        return resp?.data?.data || resp?.data || {};
    } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to fetch crypto quote");
        throw error;
    }
};

export const fetchSupportedCurrencies = async (opts = {}) => {
    const { normalize = false, businessId, currencyFrom } = opts;

    try {
        const resp = await axios.get(ENDPOINTS.getStablecoinToFiatCurrencies, {
            params: {
                currency: String(currencyFrom || "").toUpperCase(),
            },
            headers: businessId ? { "x-business-id": businessId } : undefined,
        });

        const raw = resp?.data?.data || resp?.data || [];

        if (!normalize) return raw;

        return Array.isArray(raw)
            ? raw.map((c) => ({
                  code: c.code || c.ticker || c.currency || c.symbol || "",
                  name: c.name || c.fullName || c.label || "",
                  symbol: c.symbol || c.code || "",
                  decimals: typeof c.decimals === "number" ? c.decimals : c.precision || 6,
                  ...c,
              }))
            : [];
    } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to fetch supported currencies");
        return [];
    }
};

export const fetchCryptoRate = async ({ fiatCurrency, cryptoCurrency, businessId }) => {
    try {
        const url = `/v1/wallets/crypto/rate/${encodeURIComponent(
            fiatCurrency
        )}/${encodeURIComponent(cryptoCurrency)}`;
        const resp = await axios.get(url, {
            headers: { "x-business-id": businessId },
        });
        return resp?.data?.data ?? resp?.data;
    } catch (error) {
        console.error("Error fetching crypto rate:", error);
        return error?.response?.data || { message: "Failed to fetch crypto rate" };
    }
};

export async function fetchTransferFees({ businessId, params, currency, amount } = {}) {
    try {
        const stored = typeof window !== "undefined" ? localStorage.getItem("default") : null;
        const currentCurrency = currency || (params && params.currency) || stored || undefined;
        const currentAmount = amount ?? (params && params.amount) ?? undefined;
        const query = {
            ...(params || {}),
            ...(currentCurrency ? { currency: currentCurrency } : {}),
            ...(currentAmount !== undefined && currentAmount !== null && currentAmount !== "" ? { amount: currentAmount } : {}),
        };
        const res = await axios.get("/v1/wallets/transferFees", {
            params: query,
            headers: businessId ? { "x-business-id": businessId } : undefined,
        });
        return res?.data?.data ?? res?.data ?? null;
    } catch (err) {
        console.error("fetchTransferFees failed:", err?.response?.data || err.message);
        return null;
    }
}

export async function handleFetchFeeds({ businessId, currency, page = 1, limit = 50 }) {
    try {
        const res = await axios.get(`/v1/dashboard/feeds`, {
            headers: businessId ? { "x-business-id": businessId } : undefined,
            params: { currency, page, limit },
        });
        const data = res?.data?.data;
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error("Failed to fetch feeds:", error?.response?.data || error.message);
        return [];
    }
}
export async function handleFetchDashboardStats({ businessId, currency } = {}) {
    try {
        const url = ENDPOINTS?.getDashboardStats || "/v1/dashboard/stat";
        const resp = await axios.get(url, {
            headers: businessId ? { "x-business-id": businessId } : undefined,
            params: { currency },
        });
        return resp?.data?.data ?? resp?.data ?? null;
    } catch (error) {
        console.error("Failed to fetch dashboard stats:", error?.response?.data || error.message);
        return error?.response?.data || { message: "Failed to fetch dashboard stats" };
    }
}

export const fetchBusinessStates = async ({ businessId, normalize = true } = {}) => {
    try {
        const resp = await axios.get("/v1/business/states", {
            headers: businessId ? { "x-business-id": businessId } : undefined,
        });

        const raw = resp?.data?.states ?? resp?.data?.data ?? resp?.data ?? [];
        if (!normalize) return raw;

        return Array.isArray(raw)
            ? raw.map((s) => ({
                  value: String(s.state || s.subdivision || ""),
                  label: String(s.state || ""),
                  code: s.subdivision || "",
                  country: s.country || "",
                  postal_code: s.postal_code ?? "",
              }))
            : [];
    } catch (error) {
        console.error("Failed to fetch states:", error?.response?.data || error.message);
        return [];
    }
};

export const fetchBusinessNature = async ({ businessId, normalize = true } = {}) => {
    try {
        const resp = await axios.get("/v1/business/business-nature", {
            headers: businessId ? { "x-business-id": businessId } : undefined,
        });

        const raw = resp?.data?.nature ?? resp?.data?.data ?? resp?.data ?? [];
        if (!normalize) return raw;
        return Array.isArray(raw)
            ? raw.map((n) => ({
                  value: String(n.code || n.nature || ""),
                  label: String(n.nature || ""),
                  code: String(n.code || ""),
                  nature: String(n.nature || ""),
              }))
            : [];
    } catch (error) {
        console.error(
            "Failed to fetch nature of business:",
            error?.response?.data || error.message
        );
        return [];
    }
};

export const fetchKYSStatus = async ({ businessId } = {}) => {
    try {
        const config = businessId ? { headers: { "x-business-id": businessId } } : undefined;
        const resp = await axios.post("/v1/business/get-kyc-status", {}, config);

        const top = resp?.data;
        const inner = top?.data;

        if (top && typeof top === "object" && ("status" in top || "stage" in top)) {
            const { status = "", stage = null, data = [] } = top;
            return { status, stage, data: Array.isArray(data) ? data : [] };
        }

        if (
            inner &&
            typeof inner === "object" &&
            !Array.isArray(inner) &&
            ("status" in inner || "stage" in inner || "data" in inner)
        ) {
            const { status = "", stage = null, data = [] } = inner;
            return { status, stage, data: Array.isArray(data) ? data : [] };
        }

        if (Array.isArray(inner)) {
            return { status: "", stage: null, data: inner };
        }

        if (Array.isArray(top)) {
            return { status: "", stage: null, data: top };
        }

        return { status: "", stage: null, data: [] };
    } catch (error) {
        console.error("Failed to fetch KYC status:", error?.response?.data || error.message);
        return { status: "", stage: null, data: [] };
    }
};

export const updateDirectorDetails = async ({ businessId, body }) => {
    const headers = businessId ? { "x-business-id": businessId } : undefined;

    const { data } = await axios.put(
        "/v1/kyb/update/director-details/",
        body,
        headers ? { headers } : undefined
    );

    return data;
};

export const updateBusinessDetails = async ({ businessId, body }) => {
    if (!businessId) throw new Error("updateBusinessDetails: businessId is required");
    try {
        const resp = await axios.put("/v1/business/update/business-details", body, {
            headers: { "x-business-id": businessId },
        });
        return resp?.data;
    } catch (err) {
        throw err?.response?.data || err;
    }
};

export async function generateAgreementLink({ businessId, redirectUri } = {}) {
    if (!businessId) throw new Error("businessId is required");
    const res = await axios.get("/v1/wallets/agreement-link", {
        headers: { "x-business-id": businessId },
        params: redirectUri ? { redirect_uri: redirectUri } : undefined,
    });

    const raw = res?.data ?? res;
    const url =
        (typeof raw === "string" && raw) ||
        raw?.url ||
        raw?.link ||
        raw?.data?.url ||
        raw?.data?.link;

    if (!url) throw new Error("No agreement link found in response");
    return url;
}

export async function fetchFxRate({ fromCurrency, toCurrency, amount, paymentRail, businessId }) {
    const safeAmount =
        amount === undefined || amount === null || Number.isNaN(Number(amount))
            ? 0
            : Number(amount);
    const usdPaymentRail =
      String(toCurrency || "").toUpperCase() === "USD" ? paymentRail || "ach" : "";
    try {
        const res = await axios.get("/v1/wallets/fx/rates", {
            params: {
                fromCurrency: String(fromCurrency || "").toUpperCase(),
                toCurrency: String(toCurrency || "").toUpperCase(),
                amount: safeAmount,
                ...(usdPaymentRail ? { paymentRail: usdPaymentRail } : {}),
            },
            headers: businessId ? { "x-business-id": businessId } : undefined,
        });

        const top = res?.data;
        const payload = top?.data ?? {};

        const roundedRate = Number(payload?.roundedRate);
        const rateKey = String(payload?.key ?? "");
        const isOTC = Boolean(payload?.isOTC);
        const fromCur = String(payload?.fromCurrency ?? fromCurrency ?? "").toUpperCase();
        const toCur = String(payload?.toCurrency ?? toCurrency ?? "").toUpperCase();

        if (!Number.isFinite(roundedRate) || roundedRate <= 0 || !rateKey) {
            const message = top?.message || "Invalid FX quote returned";
            const err = new Error(message);
            err.response = { data: top };
            throw err;
        }

        return { roundedRate, rateKey, isOTC, fromCurrency: fromCur, toCurrency: toCur };
    } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "Failed to fetch FX rate";
        const err = new Error(msg);
        err.cause = e;
        throw err;
    }
}

export const fetchOpeningFees = async () => {
  try {
    const response = await axios.get(ENDPOINTS.openingFees);
    return response?.data?.data;
  } catch (error) {
    console.error("Failed to fetch opening fees:", error);
    return [];
  }
};
